import React, { Fragment, useEffect, useState } from "react";
import { createReactTableColumnHelper } from "../../../components/src/react-table/features";
import { gql, useQuery, useMutation } from "urql";
import { Link, useLocation, useParams, useSearchParams } from "wouter";
import { sortBy } from "lodash";
import { cloneDeep } from "lodash";

import { Ban, Check, RotateCcw } from "lucide-react";

import { twoDecPriceFormat, numberFormat } from "../../../common/src/utils/FormatFunctions";
import { Panel } from "../../../components/src/common/Panel";
import { YesNoModal } from "../../../components/src/YesNoModal";
import { DateForm } from "../../../components/src/dateForm";
import { dateFormater, serializeSwedenDate } from "../../../components/src/dateFormater";
import { MiniTransactionForm } from "../containers/MiniTransactionForm";
import { BrokerTransactionForm } from "../containers/BrokerTransactionForm";
import { useQueryState } from "../../../components/src/use-query-state";
import { BrokerTransactionStatus, PermissionAssetEnum } from "../types.generated";
import { GET_ME } from "../common/queries";
import { ICommandBarMenuItem } from "../../../components/src/common/CommandBar";
import { ReactDataGrid } from "../../../components/src/ReactDataGrid";

const GET_BROKER_CONFIRMATIONS = gql`
    query brokerTransactions($filter: BrokerTransactionFilterInput) {
        brokerTransactions(filter: $filter) {
            _id
            broker {
                _id
                name
            }
            client {
                _id
                name
            }
            type
            description
            isin
            instrument {
                _id
                name
            }
            price
            quantity
            commission
            stampDuty
            currency
            settlementAmount
            tradeDate
            valueDate
            reportId
            externalId
            correspondingTransactionId
            error
            status
            updateTimestamp
            updateUserInfo {
                name
            }
        }
    }
`;

const UPDATE_STATUS_BROKER_CONFIRMATIONS = gql`
    mutation UpdateBrokerTransaction($input: UpdateBrokerTransactionInput!) {
        updateBrokerTransaction(input: $input) {
            _id
            status
        }
    }
`;

const CONFIRMATION_GROUPS = [BrokerTransactionStatus.Pending, BrokerTransactionStatus.Confirmed];

const groupByStatus = (confirmations, confirmationGroups) => {
    const result = {};
    confirmationGroups.forEach((status) => {
        result[status] = [];
    });

    confirmations = sortBy(confirmations, "exDate").reverse();
    confirmations.forEach((swift) => {
        if (confirmationGroups.includes(swift.status)) {
            result[swift.status].push(swift);
        }
    });
    const optimalConfirmationGroups = cloneDeep(confirmationGroups);
    const optimalResult = cloneDeep(result);
    const emptyStatuses = [];
    for (const key in result) {
        if (result[key].length === 0) {
            delete optimalResult[key];
            emptyStatuses.push(key);
        }
    }
    for (let i = optimalConfirmationGroups.length - 1; i >= 0; i--) {
        const status = optimalConfirmationGroups[i];
        if (emptyStatuses.includes(status)) {
            optimalConfirmationGroups.splice(i, 1);
        }
    }
    return [optimalResult, optimalConfirmationGroups];
};

export const BrokerConfirmationsUpdateGrid = ({ items, onStatusUpdated }): React.ReactElement => {
    const [queryArgs] = useSearchParams();
    const [selected, setSelected] = useState(null);
    const [modal, setModal] = useState({ showModal: false, payload: null });
    const [stateUpdate, updateConfirmationStatus] = useMutation(UPDATE_STATUS_BROKER_CONFIRMATIONS);

    useEffect(() => {
        if (stateUpdate && stateUpdate.data && !stateUpdate.error && onStatusUpdated) {
            onStatusUpdated();
        }
    }, [onStatusUpdated, stateUpdate]);

    const onSelectedChange = (e) => {
        setSelected(e.map((i) => i._id));
    };

    const updateStatus = async (itemIds: string[], newStatus) => {
        const allUpdates = itemIds.map(
            async (id: string) =>
                await updateConfirmationStatus({
                    input: { _id: id, status: newStatus }
                })
        );
        await Promise.all(allUpdates);
    };

    const columnHelper = createReactTableColumnHelper<any>();

    const columns = [
        columnHelper.accessor("updateTimestamp", {
            header: "Update timestamp",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                return dateFormater(value);
            }
        }),
        columnHelper.accessor("type", {
            header: "Type",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                return (
                    <Link
                        to={`/brokerconfirmations/brokertransaction/${cellProps.row.original._id}${queryArgs.toString() ? `?${queryArgs.toString()}` : ""}`}
                    >
                        {value}
                    </Link>
                );
            }
        }),
        columnHelper.accessor("broker", {
            header: "Broker",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                return value && cellProps.row.original.broker._id ? (
                    <Link to={`/parties/${cellProps.row.original.broker._id}`}>{cellProps.row.original.broker.name}</Link>
                ) : null;
            }
        }),
        columnHelper.accessor("client", {
            header: "Client",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                return value && cellProps.row.original.client._id ? (
                    <Link to={`/parties/${cellProps.row.original.client._id}`}>{cellProps.row.original.client.name}</Link>
                ) : null;
            }
        }),
        columnHelper.accessor("instrument", {
            header: "Instrument",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                return value && cellProps.row.original.instrument._id ? (
                    <Link to={`/instruments/${cellProps.row.original.instrument._id}`}>{cellProps.row.original.instrument.name}</Link>
                ) : null;
            }
        }),
        columnHelper.accessor("price", {
            header: "Price",
            cell: (cellProps) => {
                return <div style={{ textAlign: "right" }}>{numberFormat(cellProps.getValue(), "# ##0.#######")}</div>;
            }
        }),
        columnHelper.accessor("quantity", {
            header: "Quantity",
            cell: (cellProps) => {
                return <div style={{ textAlign: "right" }}>{twoDecPriceFormat(cellProps.getValue())}</div>;
            }
        }),
        columnHelper.accessor("commission", {
            header: "Commission",
            cell: (cellProps) => {
                return <div style={{ textAlign: "right" }}>{twoDecPriceFormat(cellProps.getValue())}</div>;
            }
        }),
        columnHelper.accessor("stampDuty", {
            header: "Stamp duty",
            cell: (cellProps) => {
                return <div style={{ textAlign: "right" }}>{twoDecPriceFormat(cellProps.getValue())}</div>;
            }
        }),
        columnHelper.accessor("settlementAmount", {
            header: "Settlement amount",
            cell: (cellProps) => {
                return <div style={{ textAlign: "right" }}>{twoDecPriceFormat(cellProps.getValue())}</div>;
            }
        }),
        columnHelper.accessor("currency", {
            header: "Currency",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                return value;
            }
        }),
        columnHelper.accessor("tradeDate", {
            header: "Trade date",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                return cellProps.row.original.tradeTimestamp ? serializeSwedenDate(cellProps.row.original.tradeTimestamp) : value;
            }
        }),
        columnHelper.accessor("valueDate", {
            header: "Value date",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                return value;
            }
        }),
        columnHelper.accessor("status", {
            header: "Status",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                return value;
            }
        }),
        columnHelper.accessor("download", {
            header: "Original",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                return value;
            }
        }),
        columnHelper.accessor("externalId", {
            header: "External id",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                const args = queryArgs.toString() ? `?${queryArgs.toString()}` : "";
                return cellProps.row.original._id ? (
                    <Link to={`/brokerconfirmations/brokertransaction/${cellProps.row.original._id}${args}`}>{value}</Link>
                ) : null;
            }
        }),
        columnHelper.accessor("description", {
            header: "Description",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                return value;
            }
        }),
        columnHelper.accessor("error", {
            header: "Error",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                return value;
            }
        }),
        columnHelper.accessor("correspondingTransactionId", {
            header: "Transaction",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                return cellProps.row.original.correspondingTransactionId ? (
                    <Link
                        to={`/brokerconfirmations/minitransaction/${cellProps.row.original.correspondingTransactionId}${
                            queryArgs.toString() ? `?${queryArgs.toString()}` : ""
                        }`}
                    >
                        {value}
                    </Link>
                ) : null;
            }
        }),
        columnHelper.accessor("updateUserInfo", {
            header: "Update user info",
            cell: (cellProps) => {
                return cellProps.row.original.updateUserInfo ? cellProps.row.original.updateUserInfo.name : null;
            }
        })
    ];

    const commands: ICommandBarMenuItem[] =
        selected && selected.length > 0
            ? [
                  {
                      itemKey: "newItem",
                      text: "Change selection status",
                      subMenuProps: {
                          items: [
                              {
                                  itemKey: "pending",
                                  text: "Change to Pending",
                                  icon: <RotateCcw />,
                                  onClick: () => {
                                      updateStatus(selected, "Pending");
                                  }
                              },
                              {
                                  itemKey: "confirmed",
                                  text: "Change to Confirmed",
                                  icon: <Check />,
                                  onClick: () => {
                                      updateStatus(selected, "Confirmed");
                                  }
                              },
                              {
                                  itemKey: "deleted",
                                  text: "Change to Deleted",
                                  icon: <Ban />,
                                  onClick: () => {
                                      setModal({
                                          showModal: true,
                                          payload: { newStatus: "Deleted", ids: selected }
                                      });
                                  }
                              }
                          ]
                      }
                  }
              ]
            : null;

    return (
        <div>
            {modal.showModal ? (
                <YesNoModal
                    warningText={"Are you sure you want to delete selected broker confirmations?"}
                    modal={{
                        showModal: modal.showModal,
                        payload: modal.payload
                    }}
                    setModal={setModal}
                    onYes={() => {
                        updateStatus(selected, "Deleted");
                    }}
                />
            ) : null}

            <ReactDataGrid
                key={items}
                columns={columns}
                data={items}
                commands={commands}
                defaultHiddenColumns={null}
                exportToXlsxFile={null}
                onSelectedChange={onSelectedChange}
            />
        </div>
    );
};

export const BrokerConfirmationsPage = ({ startDate }: { startDate: string }): React.ReactElement => {
    // All brokerConfirmations except deleted
    const [{ fetching: loading, error, data }, refetch] = useQuery({
        query: GET_BROKER_CONFIRMATIONS,
        variables: { filter: { statusIn: CONFIRMATION_GROUPS, startDate: startDate } },
        requestPolicy: "network-only"
    });

    if (loading) return <p>Loading broker confirmations</p>;
    if (error) return <p>error: {JSON.stringify(error, null, 2)}</p>;

    const allConfirmations = data.brokerTransactions;
    const [groups, optimalConfirmationGroups] = groupByStatus(allConfirmations, CONFIRMATION_GROUPS);

    return (
        <div>
            {optimalConfirmationGroups.map((status) => (
                <div key={status} className="mt-4">
                    <h2>{status}</h2>
                    <BrokerConfirmationsUpdateGrid key={groups[status]} items={groups[status]} onStatusUpdated={refetch} />
                </div>
            ))}
        </div>
    );
};

export function BrokerConfirmationsCompletePage(): React.ReactElement {
    const [queryArgs] = useSearchParams();
    const { id, type } = useParams<{ id: string; type: string }>();
    const [startDate] = useQueryState("startDate", serializeSwedenDate(new Date(new Date().getFullYear(), 0, 1)));

    const [{ fetching: loadingMe, error: errorMe, data }] = useQuery({
        query: GET_ME,
        requestPolicy: "network-only"
    });

    const [, navigate] = useLocation();

    if (loadingMe) return <div>Loading...</div>;

    if (errorMe)
        return (
            <div>
                <p>Error:</p>
                <pre> {JSON.stringify(errorMe, null, 2)}</pre>
            </div>
        );

    return (
        <Fragment>
            <div className="flex flex-wrap -mx-2">
                <div className="flex w-full md:w-1/2 px-2 mb-4">
                    <DateForm defaultDateString={startDate} dateName={"startDate"}></DateForm>
                </div>
                <div className="w-full md:w-1/2 px-2 mb-4 text-right">
                    {data &&
                    data.me &&
                    data.me.frontendRole &&
                    data.me.frontendRole.assets &&
                    (data.me.frontendRole.assets.includes(PermissionAssetEnum.BackOffice) || data.me.frontendRole.assets.length === 0) ? (
                        <a
                            href="https://github.com/CaptorAB/documents/blob/master/BackOffice/manual/transacation_and_trade_reconciliation.md"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Manual
                        </a>
                    ) : null}
                </div>
            </div>
            <div className="flex flex-wrap -mx-2">
                <div className={"w-full px-2 mb-4"}>
                    <BrokerConfirmationsPage startDate={startDate} />
                </div>
            </div>

            {id && type === "minitransaction" ? (
                <Panel
                    isOpen={id != null}
                    onDismiss={() => navigate(`/brokerconfirmations${queryArgs.toString() ? `?${queryArgs.toString()}` : ""}`)}
                >
                    <MiniTransactionForm id={id} type={null} showHeader />
                </Panel>
            ) : null}
            {id && type === "brokertransaction" ? (
                <Panel
                    isOpen={id != null}
                    onDismiss={() => navigate(`/brokerconfirmations${queryArgs.toString() ? `?${queryArgs.toString()}` : ""}`)}
                    title={"Broker Confirmation"}
                >
                    <BrokerTransactionForm id={id} startDate={startDate} />
                </Panel>
            ) : null}
        </Fragment>
    );
}
